/**
 * Plan comparison grid (FR-BILLING-01/02).
 *
 * One card per catalog tier, cheapest first, each carrying its price for the
 * selected cadence, its hard caps and the full feature checklist — so the four
 * tiers can be compared column-by-column on desktop and by scrolling on a
 * phone. The current tier is badged and its button is inert; everything above
 * it offers checkout, everything below it offers a confirmed downgrade.
 *
 * A tenant on a negotiated price sheet gets no self-service buttons at all:
 * changing a contract is a conversation, not a click.
 */
import { DeskSection } from '../../components/desk-section';
import type { BillingCadence, BillingPlan } from '../../lib/api-types';

import {
  FEATURE_LABELS,
  limitLabel,
  LIMIT_ORDER,
  cadenceSuffix,
  formatLimit,
  formatMoneyCents,
} from './billing-format';

const CADENCES: readonly BillingCadence[] = ['monthly', 'annual'];

function priceForCadence(plan: BillingPlan, cadence: BillingCadence): number | null {
  return cadence === 'annual' ? plan.price_annual_cents : plan.price_monthly_cents;
}

export interface PlanComparisonProps {
  /** Catalog tiers, already sorted cheapest → most expensive. */
  plans: readonly BillingPlan[];
  currentPlanId: string;
  cadence: BillingCadence;
  onCadenceChange: (cadence: BillingCadence) => void;
  /** Paid upgrade → hosted checkout. */
  onUpgrade: (plan: BillingPlan) => void;
  /** Opens the consequence-stating downgrade confirmation. */
  onDowngrade: (plan: BillingPlan) => void;
  /** Custom price sheet: show the tiers, disable the buttons. */
  isCustomContract?: boolean;
}

export function PlanComparison({
  plans,
  currentPlanId,
  cadence,
  onCadenceChange,
  onUpgrade,
  onDowngrade,
  isCustomContract = false,
}: PlanComparisonProps): JSX.Element {
  const currentIndex = plans.findIndex((plan) => plan.plan_id === currentPlanId);

  return (
    <div id="plan-comparison" className="billing-compare">
      <DeskSection
        title="Compare plans"
        className="billing-card"
        actions={
          <div className="cadence-toggle" role="group" aria-label="Billing cadence">
            {CADENCES.map((option) => (
              <button
                key={option}
                type="button"
                className="cadence-toggle__option"
                aria-pressed={cadence === option}
                onClick={() => onCadenceChange(option)}
              >
                {option === 'monthly' ? 'Monthly' : 'Annual'}
              </button>
            ))}
          </div>
        }
      >
        <div className="plan-grid">
          {plans.map((plan, index) => {
            const isCurrent = plan.plan_id === currentPlanId;
            const price = priceForCadence(plan, cadence);
            const isUpgrade = index > currentIndex;
            return (
              <article
                key={plan.plan_id}
                className={`plan-card${isCurrent ? ' plan-card--current' : ''}`}
                aria-label={`${plan.display_name} plan`}
              >
                <header className="plan-card__head">
                  <h3 className="plan-card__name">{plan.display_name}</h3>
                  {isCurrent ? <span className="badge badge--brand">Current</span> : null}
                </header>
                <p className="plan-card__price">
                  {price === null ? (
                    <span className="muted">Annual billing not offered</span>
                  ) : (
                    <>
                      <span className="plan-card__amount">{formatMoneyCents(price)}</span>
                      {price === 0 ? null : <span className="muted">{cadenceSuffix(cadence)}</span>}
                    </>
                  )}
                </p>
                <ul className="plan-card__caps">
                  {LIMIT_ORDER.map((key) => (
                    <li key={key}>
                      <span className="muted">{limitLabel(key)}</span>
                      <span>{formatLimit(key, plan.limits[key as keyof typeof plan.limits])}</span>
                    </li>
                  ))}
                </ul>
                <ul className="plan-card__features">
                  {Object.keys(FEATURE_LABELS).map((key) => (
                    <li key={key} className={plan.features[key] === true ? 'is-on' : 'is-off'}>
                      <span aria-hidden="true">{plan.features[key] === true ? '✓' : '—'}</span>
                      {/* Text label, not colour or glyph alone (WCAG 1.4.1). */}
                      <span className="visually-hidden">
                        {plan.features[key] === true ? 'Included:' : 'Not included:'}
                      </span>{' '}
                      {FEATURE_LABELS[key]}
                    </li>
                  ))}
                </ul>
                <div className="plan-card__action">
                  {isCurrent ? (
                    <button type="button" className="btn btn--secondary btn--sm" disabled>
                      Current plan
                    </button>
                  ) : isCustomContract ? (
                    <button type="button" className="btn btn--secondary btn--sm" disabled>
                      Managed by contract
                    </button>
                  ) : isUpgrade ? (
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      onClick={() => onUpgrade(plan)}
                    >
                      Upgrade now
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => onDowngrade(plan)}
                    >
                      Downgrade
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </DeskSection>
    </div>
  );
}
